import { Navigation, TrainFront, Footprints, MapPin, ExternalLink } from "lucide-react";
import {
  Reveal,
  SectionHeading,
  ADDRESS,
  MAP_EMBED_URL,
  DIRECTIONS_FROM_MRT_URL,
  MAP_LOCATION_URL,
} from "./shared";

const STEPS = [
  {
    icon: TrainFront,
    title: "Alight at Kembangan MRT (EW6)",
    detail: "Take the East–West Line and exit towards Jalan Kembangan / Lengkong Tiga.",
  },
  {
    icon: Footprints,
    title: "Walk ~3 minutes north",
    detail: "Head along Jalan Kembangan. The workshop is a short, flat, sheltered walk away.",
  },
  {
    icon: MapPin,
    title: "Arrive at 52 Jalan Kembangan",
    detail: "Look for the AFZ Tech Solution signage. Drop-offs & quick diagnostics welcome.",
  },
];

export const WorkshopMap = () => {
  return (
    <section
      id="location"
      data-testid="workshop-map-section"
      className="relative py-24 sm:py-32 bg-void overflow-hidden"
    >
      <div className="absolute -top-24 right-1/4 w-[28rem] h-[28rem] rounded-full bg-brand-cyan/8 blur-[150px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          eyebrow="Find The Workshop"
          title="52 Jalan Kembangan"
          accent="on the map"
          sub="Our repair bench is a short walk from Kembangan MRT. Tap for live, turn-by-turn directions straight from the station to our door."
        />

        <div className="mt-14 grid lg:grid-cols-5 gap-8 items-stretch">
          {/* Interactive map */}
          <Reveal className="lg:col-span-3">
            <div className="group relative h-full rounded-2xl overflow-hidden border border-slate-800 clip-corner bg-card/80">
              <div className="absolute inset-0 pointer-events-none z-10 ring-1 ring-inset ring-brand-lime/20 rounded-2xl transition-all duration-500 group-hover:ring-brand-lime/50" />
              <div className="absolute top-4 left-4 z-20 pointer-events-none flex items-center gap-2 bg-void/85 backdrop-blur-md border border-slate-700 rounded-full px-4 py-2">
                <span className="w-2 h-2 rounded-full bg-brand-lime animate-pulse" />
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white">
                  AFZ Tech Solution
                </span>
              </div>
              <iframe
                title="AFZ Tech Solution — 52 Jalan Kembangan workshop location"
                data-testid="workshop-map-iframe"
                src={MAP_EMBED_URL}
                className="w-full h-full min-h-[380px] lg:min-h-[520px] grayscale-[0.2] contrast-[1.05]"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>

          {/* Directions from MRT */}
          <Reveal delay={0.12} className="lg:col-span-2">
            <div className="h-full bg-card/80 border border-slate-800 rounded-2xl p-8 clip-corner flex flex-col">
              <div className="flex items-center gap-3">
                <Navigation className="w-6 h-6 text-brand-lime shrink-0" />
                <h3 className="font-display text-2xl font-bold uppercase text-white leading-none">
                  From Kembangan MRT
                </h3>
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-brand-cyan mt-2">
                ~3 min walk · EW6 East–West Line
              </p>

              <ol className="mt-8 flex-1 space-y-6">
                {STEPS.map((s, i) => (
                  <li key={s.title} className="flex items-start gap-4" data-testid={`map-step-${i}`}>
                    <div className="relative shrink-0">
                      <div className="w-10 h-10 rounded-xl border border-slate-700 bg-void flex items-center justify-center">
                        <s.icon className="w-5 h-5 text-brand-lime" />
                      </div>
                      {i < STEPS.length - 1 && (
                        <span className="absolute left-1/2 top-10 -translate-x-1/2 h-6 w-px bg-slate-700" />
                      )}
                    </div>
                    <div>
                      <p className="font-display text-sm font-bold uppercase tracking-wide text-white">
                        {s.title}
                      </p>
                      <p className="text-slate-400 text-sm mt-1 leading-relaxed">{s.detail}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="mt-8 space-y-3">
                <a
                  data-testid="workshop-directions-button"
                  href={DIRECTIONS_FROM_MRT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-3 bg-brand-lime text-void font-display font-bold uppercase text-base px-6 py-4 rounded-full hover:bg-brand-limedark hover:scale-[1.02] transition-all duration-300 shadow-[0_0_30px_rgba(170,255,0,0.4)]"
                >
                  <Navigation className="w-5 h-5" /> Get Directions
                </a>
                <a
                  data-testid="workshop-openmaps-link"
                  href={MAP_LOCATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 border border-slate-700 text-slate-300 font-mono text-[12px] uppercase tracking-[0.15em] px-6 py-3 rounded-full hover:border-brand-lime hover:text-brand-lime transition-all duration-300"
                >
                  <ExternalLink className="w-4 h-4" /> {ADDRESS}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
